import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertTriangle } from "@/lib/icons";
import {
  getNetworkCalls,
  subscribeNetworkCalls,
  type NetworkCall,
} from "@/lib/networkGuard";

/**
 * Per-route breakdown of outbound calls observed by the runtime privacy
 * guard. Groups the visited pathnames into the user-facing tabs the
 * privacy checklist promises about (Play, Learn, Settings/A11y, Other),
 * so a regression on any single tab is immediately attributable.
 *
 * Only counts app-originated calls - host/platform calls are surfaced
 * separately in NetworkGuardIndicator.
 */

type GroupKey = "play" | "learn" | "settings" | "other";

const GROUPS: { key: GroupKey; label: string; match: (route: string) => boolean }[] = [
  {
    key: "play",
    label: "Play (games)",
    match: (r) => r === "/play" || r.startsWith("/play/"),
  },
  {
    key: "learn",
    label: "Learn (flashcards)",
    match: (r) => r === "/learn" || r.startsWith("/learn/"),
  },
  {
    key: "settings",
    label: "Settings & Accessibility",
    match: (r) => r === "/accessibility" || r === "/privacy",
  },
  {
    key: "other",
    label: "Other tabs (Lab, Tools, Explore, ...)",
    match: () => true, // catch-all
  },
];

function groupOf(route: string): GroupKey {
  for (const g of GROUPS) if (g.match(route)) return g.key;
  return "other";
}

export default function RouteNetworkReport() {
  const [calls, setCalls] = useState<ReadonlyArray<NetworkCall>>(() =>
    getNetworkCalls(),
  );
  const [visited, setVisited] = useState<Set<string>>(() => {
    const s = new Set<string>();
    if (typeof window !== "undefined") s.add(window.location.pathname);
    return s;
  });

  useEffect(() => subscribeNetworkCalls(setCalls), []);

  // Track which routes the user has actually navigated through this
  // session, so a "0 calls" row reads as "verified clean", not "untested".
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mark = () => {
      setVisited((prev) => {
        const next = new Set(prev);
        next.add(window.location.pathname);
        return next;
      });
    };
    window.addEventListener("popstate", mark);
    // Patch pushState/replaceState so SPA navigations register too.
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
      const r = origPush.apply(this, args as Parameters<typeof history.pushState>);
      mark();
      return r;
    };
    history.replaceState = function (...args) {
      const r = origReplace.apply(
        this,
        args as Parameters<typeof history.replaceState>,
      );
      mark();
      return r;
    };
    return () => {
      window.removeEventListener("popstate", mark);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }, []);

  const rows = useMemo(() => {
    const appCalls = calls.filter((c) => c.source === "app");
    return GROUPS.map((g) => {
      const groupCalls = appCalls.filter((c) => groupOf(c.route) === g.key);
      const visitedRoutes = Array.from(visited).filter((r) => {
        if (g.key === "other") return groupOf(r) === "other";
        return g.match(r);
      });
      return {
        ...g,
        count: groupCalls.length,
        visited: visitedRoutes,
        sampleCalls: groupCalls.slice(-5),
      };
    });
  }, [calls, visited]);

  return (
    <div className="rounded-lg border border-border bg-card/40 p-3">
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Per-tab runtime report
      </h3>
      <p className="text-[11px] text-muted-foreground leading-snug mb-3">
        Open Play, Learn, and Settings in this browser session, then return
        here. Each row shows how many outbound calls our app code made while
        you were on that tab. It should stay at 0 everywhere.
      </p>
      <ul className="space-y-2">
        {rows.map((r) => {
          const clean = r.count === 0;
          const tested = r.visited.length > 0;
          return (
            <li
              key={r.key}
              className={`rounded-md border p-2 ${
                clean
                  ? "border-primary/20 bg-primary/5"
                  : "border-destructive/40 bg-destructive/10"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {clean ? (
                    <CheckCircle2
                      size={14}
                      className="text-primary shrink-0"
                      aria-hidden="true"
                    />
                  ) : (
                    <AlertTriangle
                      size={14}
                      className="text-destructive shrink-0"
                      aria-hidden="true"
                    />
                  )}
                  <span className="text-xs font-semibold text-foreground truncate">
                    {r.label}
                  </span>
                </div>
                <span
                  className={`text-[11px] font-mono px-2 py-0.5 rounded-full border ${
                    clean
                      ? "border-primary/30 text-foreground"
                      : "border-destructive/40 text-destructive"
                  }`}
                >
                  {r.count} call{r.count === 1 ? "" : "s"}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                {tested ? (
                  <>
                    Visited this session:{" "}
                    <span className="font-mono">{r.visited.join(", ")}</span>
                  </>
                ) : (
                  <em>Not visited yet in this session.</em>
                )}
              </div>
              {r.sampleCalls.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {r.sampleCalls.map((c, i) => (
                    <li
                      key={i}
                      className="font-mono text-[11px] text-destructive break-all"
                    >
                      {c.api} on {c.route} &rarr; {c.url}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}